import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChannelViewClinicPetParentFragment } from 'shared/types/graphql';
import { StateSliceName } from 'state/StateSliceName';

export type ExportType = 'DownloadPdf' | 'ExportToPims';

interface ExportConversationState {
  channelId: string;
  channelPetParents: ChannelViewClinicPetParentFragment[];
  exportFileName: string;
  isExportModalOpen: boolean;
  selectedExportType: ExportType;
  shouldIncludeNotes: boolean;
}

interface ChannelViewState {
  channelId?: string;
  petParentId?: string;
  isOpen: boolean;
  draftMessageText: string;
  isDetailsModalOpen: boolean;
  isInvoicePreviewModalOpen: boolean;
}

interface ConversationsState {
  exportData: ExportConversationState;
  channelViewData: ChannelViewState;
}

const initialState: ConversationsState = {
  exportData: {
    channelId: '',
    channelPetParents: [],
    exportFileName: '',
    isExportModalOpen: false,
    selectedExportType: 'ExportToPims',
    shouldIncludeNotes: false,
  },
  channelViewData: {
    channelId: '',
    isOpen: false,
    draftMessageText: '',
    isDetailsModalOpen: false,
    isInvoicePreviewModalOpen: false,
  },
};

const conversationsSlice = createSlice({
  name: StateSliceName.Conversations,
  initialState,
  reducers: {
    resetExportData: (state) => {
      state.exportData = initialState.exportData;
    },
    updateExportData: (state, action: PayloadAction<Partial<ExportConversationState>>) => {
      Object.assign(state.exportData, action.payload);
    },
    openChannelOverlay: (
      state,
      action: PayloadAction<{ channelId?: string; petParentId?: string; draftMessageText?: string }>,
    ) => {
      state.channelViewData.channelId = action.payload.channelId;
      state.channelViewData.petParentId = action.payload.petParentId;
      state.channelViewData.isOpen = true;
      state.channelViewData.draftMessageText = action.payload.draftMessageText ?? '';
    },
    toggleConversationDetailsModal: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.channelViewData.isDetailsModalOpen = action.payload.isOpen;
    },
    toggleInvoicePreviewModal: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.channelViewData.isInvoicePreviewModalOpen = action.payload.isOpen;
    },
    closeChannelOverlay: (state) => {
      state.channelViewData = initialState.channelViewData;
    },
  },
});

export const {
  updateExportData,
  resetExportData,
  openChannelOverlay,
  closeChannelOverlay,
  toggleConversationDetailsModal,
  toggleInvoicePreviewModal,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
